<script lang="ts" generics="T">
	import { page } from '$app/state';
	import IconToggle from '@astlai/common/IconToggle.svelte';
	import RoleGate from '@astlai/common/RoleGate.svelte';
	import { pbService } from 'src/services/pb-service.svelte';
	import IconUp from 'virtual:icons/material-symbols/arrow-upward-rounded';
	import IconWeb from 'virtual:icons/material-symbols/language';
	import IconStop from 'virtual:icons/material-symbols/stop-rounded';
	import FeatureGate from './FeatureGate.svelte';
	import { t } from './i18n/tranlsator.svelte';
	import NewChatOptions from './NewChatOptions.svelte';
	import { getTenantContext } from 'src/contexts/tenant.context.svelte';

	interface Props {
		value: string;
		showSettings?: boolean;
		model?: string;
		webSearchEnabled?: boolean;
		embeddingsVendor?: string;
		onchange?: (v: string) => void;
		onabort?: () => void;
		disabled?: boolean;
		isStreaming?: boolean;
		appearance?: 'footer' | 'hero';
		placeholder?: string;
	}

	let {
		disabled,
		isStreaming = false,
		value = $bindable(),
		model = $bindable(),
		showSettings = false,
		embeddingsVendor = $bindable(),
		webSearchEnabled = $bindable(),
		onchange = () => {},
		onabort = () => {},
		appearance = 'footer',
		placeholder = t('chat.placeholder')
	}: Props = $props();

	let inputEl: HTMLTextAreaElement = $state(null);
	const tenantCtx = getTenantContext();

	const twoRowLayout = $derived(appearance === 'hero' && page.data.loggedInUser?.role !== 'user');

	const inputDisabled = $derived.by(() => {
		value; // reactive dependency
		return !inputEl?.validity.valid || disabled;
	});

	// simulate user role for testing;
	// page.data.loggedInUser.role = 'user';

	const showWebSearchToggle = $derived.by(() => {
		const tenant = page.data.tenant || tenantCtx.tenant;
		return !tenant?.feature_flags?.webSearch?.settings?.alwaysOn;
	});

	async function handleInput(e: Event) {
		e.preventDefault();
		if (value) {
			onchange(value);
		}
	}

	function handleButtonClick(e: Event) {
		e.preventDefault();
		if (isStreaming) {
			onabort();
		} else {
			handleInput(e);
		}
	}

	function handleKeydown(e: KeyboardEvent) {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleInput(e);
		}
	}

	function handleWebSearchChange(enabled: boolean) {
		pbService.updateMyself({
			settings: { ...page.data.loggedInUser?.settings, webSearchEnabled: enabled }
		});
	}
</script>

<form
	class:hero={appearance === 'hero'}
	onsubmit={handleInput}
	class="input-form"
	class:two-row-layout={twoRowLayout}
>
	<div class="textarea-wrapper" data-replicated-value={value}>
		<textarea
			bind:this={inputEl}
			required
			name="userInput"
			rows="1"
			bind:value
			{placeholder}
			onkeydown={handleKeydown}
		></textarea>
	</div>

	<div class="options">
		<FeatureGate featureFlag="webSearch">
			{#if showWebSearchToggle}
				<div class="tools">
					<IconToggle
						label={t('chat.webSearchEnabledLabel')}
						bind:checked={webSearchEnabled}
						onchange={(c) => handleWebSearchChange(c)}><IconWeb /></IconToggle
					>
				</div>
			{/if}
		</FeatureGate>

		<div class="controls">
			{#if showSettings}
				<RoleGate minimumRole="operator">
					<NewChatOptions bind:model bind:embeddingsVendor />
				</RoleGate>
			{/if}
			<button
				type="button"
				class="accent"
				class:stop={isStreaming}
				disabled={!isStreaming && inputDisabled}
				onclick={handleButtonClick}
				aria-label={isStreaming ? t('chat.stopResponse') : ''}
			>
				{#if isStreaming}
					<IconStop />
				{:else}
					<IconUp />
				{/if}
			</button>
		</div>
	</div>
</form>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.input-form {
		@include lib.flex-row();
		border-radius: 0.75rem;
		@include lib.glass-surface();

		&.two-row-layout {
			@include lib.flex-column();
			.options {
				padding: 0 1rem 1rem 1rem;
			}
			&.hero > .textarea-wrapper textarea {
				padding-bottom: 1rem;
			}
		}

		&.hero {
			> .textarea-wrapper {
				textarea {
					padding: 1.5rem;
				}
			}
		}

		.textarea-wrapper {
			display: grid;
			flex: 1;
			max-height: 12rem;
			overflow: auto;

			&::after {
				content: attr(data-replicated-value) ' ';
				white-space: pre-wrap;
				visibility: hidden;
				word-wrap: break-word;
				padding: 1rem;
				font: inherit;
				font-size: 1rem;
				line-height: 1.5rem;
				box-sizing: border-box;
				grid-area: 1 / 1 / 2 / 2;
			}

			textarea {
				grid-area: 1 / 1 / 2 / 2;
				background: transparent;
				border: none;
				color: var(--text-primary);
				font: inherit;
				font-size: 1rem;
				line-height: 1.5rem;
				padding: 1rem;
				resize: none;
				overflow-y: auto;
				box-sizing: border-box;
				field-sizing: content;
				&::placeholder {
					color: var(--text-secondary);
				}
				&:focus-visible {
					outline: none;
				}
			}
		}

		.options {
			@include lib.flex-row();
			@include lib.flex-align(space-between, center);
			gap: 0.5rem;
			padding-right: 1rem;
			padding-top: 0;

			.tools {
				@include lib.flex-row();
				gap: 1rem;
			}

			.controls {
				@include lib.flex-row();
				@include lib.flex-align(flex-end, center);
				gap: 1rem;
				flex: 1 1 auto;
				justify-self: flex-end;
			}

			button {
				flex: 0 0 auto;
				min-width: 0;
				width: 32px;
				height: 32px;
			}
		}
	}
</style>
